import apiClient from './apiClient';

/**
 * Service für den Wasserverbrauch aus Zählern zu einer Nebenkostenabrechnung
 * (`GET /api/utility-bills/{billId}/wasser-verbrauch`, Vermieter-only).
 *
 * Analog zu utilityBillCacheService.js, aber bewusst OHNE localStorage-Persistenz: Die
 * Zähler-Ableseergebnisse sind rein transiente Positions-Erfassungshilfe (nicht Teil der
 * eigentlichen Abrechnungsdaten), eine Persistenz über Page-Reloads hinweg brächte keinen
 * Mehrwert und würde nur zusätzliche Invalidierungspfade schaffen. In-Memory-Cache mit kurzer
 * TTL + Request-Deduplizierung (Cache-Stampede-Schutz) genügen hier.
 */
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 Minuten

// In-Memory Cache: billId -> { data, timestamp }
const memoryCache = new Map();
// Laufende Requests: billId -> Promise<data> (Request-Dedup)
const pendingRequests = new Map();

// Generation-Token: schützt vor Race-Conditions zwischen invalidate()/invalidateAll() und
// einer zu diesem Zeitpunkt bereits laufenden Anfrage. Jede Invalidierung erhöht den globalen
// Zähler; eine später eintreffende Antwort wird nur noch gecacht, wenn sich der Zähler seit
// Request-Start NICHT geändert hat - sonst würde eine überholte Antwort (z.B. aus der Zeit vor
// einem Zählerstand-Update) eine gerade erst invalidierte billId erneut mit veralteten Daten
// befüllen.
let generation = 0;

const isFresh = (entry) => !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;

/**
 * Lädt den Wasserverbrauch aus Zählern für eine Nebenkostenabrechnung (gecacht).
 * @param {string} billId - UUID der Abrechnung
 * @returns {Promise<object>} WasserVerbrauchDTO { zeitraumVon, zeitraumBis, einzelZaehler, gesamtZaehler }
 */
const getWasserVerbrauch = (billId) => {
  const cached = memoryCache.get(billId);
  if (isFresh(cached)) {
    return Promise.resolve(cached.data);
  }
  if (cached) {
    memoryCache.delete(billId);
  }

  if (pendingRequests.has(billId)) {
    return pendingRequests.get(billId);
  }

  const requestGeneration = generation;

  const promise = apiClient.get(`/utility-bills/${billId}/wasser-verbrauch`)
    .then((response) => {
      // Nur cachen, wenn seit Request-Start keine Invalidierung stattgefunden hat (Race-Schutz).
      if (requestGeneration === generation) {
        memoryCache.set(billId, { data: response.data, timestamp: Date.now() });
      }
      // pendingRequests nur löschen, wenn dort noch das eigene Promise steht - invalidate()
      // könnte den Slot bereits geräumt haben bzw. ein neuerer Request könnte ihn längst
      // überschrieben haben.
      if (pendingRequests.get(billId) === promise) {
        pendingRequests.delete(billId);
      }
      return response.data;
    })
    .catch((err) => {
      if (pendingRequests.get(billId) === promise) {
        pendingRequests.delete(billId);
      }
      throw err;
    });

  pendingRequests.set(billId, promise);
  return promise;
};

/**
 * Invalidiert den Cache-Eintrag für eine bestimmte Abrechnung.
 * @param {string} billId - UUID der Abrechnung
 */
const invalidate = (billId) => {
  generation++;
  memoryCache.delete(billId);
  pendingRequests.delete(billId);
};

/**
 * Invalidiert den kompletten Cache (z.B. nach Zählerstand-Mutationen, deren billId hier nicht
 * bekannt ist, oder bei Logout/Benutzerwechsel).
 */
const invalidateAll = () => {
  generation++;
  memoryCache.clear();
  pendingRequests.clear();
};

/**
 * Löscht den kompletten Cache (Service-Konvention, siehe utilityBillCacheService/
 * zaehlerCacheService). Wird von authService.clearAllUserCaches() beim Login/Logout/
 * Benutzerwechsel aufgerufen, damit Zählerverbrauchsdaten keine Session überleben.
 */
const clearCache = () => {
  invalidateAll();
};

const wasserVerbrauchService = {
  getWasserVerbrauch,
  invalidate,
  invalidateAll,
  clearCache
};

export default wasserVerbrauchService;
